// src/utils/energyCalculations.js
/**
 * Unified energy usage prediction calculation utility
 * This centralizes all prediction logic to ensure consistency across the application
 */

/**
 * Calculates the predicted total energy usage for the energy year based on actual usage so far
 * 
 * @param {number} actualUsageSoFar - The actual energy usage accumulated so far
 * @param {string} latestMonth - The latest month with data in format '01' to '12'
 * @param {Date|string|null} lastImportDate - The date of the last ISTA data import
 * @param {boolean} isCurrentEnergyYear - Whether this is the current energy year
 * @param {Date|null} currentDate - Optional override for current date (useful for testing)
 * @returns {number} The predicted total for the energy year
 */
export const calculatePredictedTotal = (
  actualUsageSoFar,
  latestMonth,
  lastImportDate,
  isCurrentEnergyYear,
  currentDate = null
) => {
  // Input validation
  if (typeof actualUsageSoFar !== 'number' || actualUsageSoFar < 0) {
    console.warn('Invalid actualUsageSoFar provided to calculatePredictedTotal:', actualUsageSoFar);
    return actualUsageSoFar; // Return as-is if invalid
  }

  // Base seasonality map - what percentage of annual consumption occurs by each month
  const seasonalityMap = {
    '07': 0.01, '08': 0.03, '09': 0.05, '10': 0.12,
    '11': 0.24, '12': 0.40, '01': 0.57, '02': 0.71,
    '03': 0.84, '04': 0.93, '05': 0.98, '06': 1.00
  };

  // Month transition percentages - how much each month contributes to the annual total
  const monthContributions = {
    '07': 0.01, // July = 1%
    '08': 0.02, // August = 2%
    '09': 0.02, // September = 2%
    '10': 0.07, // October = 7%
    '11': 0.12, // November = 12%
    '12': 0.16, // December = 16%
    '01': 0.17, // January = 17%
    '02': 0.14, // February = 14%
    '03': 0.13, // March = 13%
    '04': 0.09, // April = 9%
    '05': 0.05, // May = 5%
    '06': 0.02  // June = 2%
  };

  // Days per month (approximate)
  const daysInMonth = {
    '01': 31, '02': 28, '03': 31, '04': 30, 
    '05': 31, '06': 30, '07': 31, '08': 31,
    '09': 30, '10': 31, '11': 30, '12': 31
  };

  // Use provided date or current date
  const now = currentDate ? new Date(currentDate) : new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // JS months are 0-indexed
  const currentDay = now.getDate();

  // Parse the last import date if it's a string
  let importDate = null;
  let importYear = null;
  let importMonth = null;
  let importDay = null;
  
  if (lastImportDate) {
    importDate = new Date(lastImportDate);
    importYear = importDate.getFullYear();
    importMonth = importDate.getMonth() + 1;
    importDay = importDate.getDate();
  }

  // Initialize the season fraction
  let seasonFraction;

  // Handle current energy year with partial month data
  if (isCurrentEnergyYear && 
      parseInt(latestMonth) === currentMonth && 
      currentMonth >= 1 && currentMonth <= 12) {
    
    // This is the current month in the current energy year - use dynamic calculation
    const monthStr = currentMonth.toString().padStart(2, '0');
    
    // Determine the effective day to use for calculation
    let effectiveDay = currentDay;
    
    // If we have a last import date and it's in the current month, use that instead
    if (importDate && importYear === currentYear && importMonth === currentMonth) {
      effectiveDay = Math.min(importDay, currentDay);
      console.log(`🔧 Using ISTA import date for ${monthStr} calculation: Day ${effectiveDay}`);
    }
    
    // Calculate what percentage of the month has passed
    const totalDaysInMonth = daysInMonth[monthStr] || 30;
    const daysPassed = Math.min(effectiveDay, totalDaysInMonth);
    const monthProgress = daysPassed / totalDaysInMonth;
    
    // Get base seasonality from previous month
    let baseSeasonality = 0;
    if (currentMonth > 1) {
      const prevMonthStr = (currentMonth - 1).toString().padStart(2, '0');
      baseSeasonality = seasonalityMap[prevMonthStr] || 0;
    } else {
      // January - use December's value
      baseSeasonality = seasonalityMap['12'] || 0;
    }
    
    // Calculate contribution for current partial month
    const monthContribution = monthContributions[monthStr] || 0;
    const partialContribution = monthContribution * monthProgress;
    
    // Calculate season fraction
    seasonFraction = baseSeasonality + partialContribution;
    
    console.log(`🔧 Dynamic calculation for ${monthStr}: Day ${effectiveDay}/${totalDaysInMonth} (${(monthProgress * 100).toFixed(1)}% through month), base: ${baseSeasonality.toFixed(3)}, contribution: ${partialContribution.toFixed(3)}, season fraction: ${seasonFraction.toFixed(4)}`);
  } else {
    // Use the standard seasonality map for completed months or past energy years
    seasonFraction = seasonalityMap[latestMonth] || 1;
  }

  // Calculate prediction based on season fraction
  let predicted;
  
  if (seasonFraction >= 1.0) {
    // At or past June (100% of energy year), prediction = actual usage
    predicted = Math.round(actualUsageSoFar);
  } else if (seasonFraction > 0) {
    // Partway through the year, extrapolate based on season
    predicted = Math.round(actualUsageSoFar / seasonFraction);
  } else {
    // Fallback case (should not happen with correct inputs)
    predicted = actualUsageSoFar;
  }

  // Debug information
  console.log(`Prediction calculation for ${latestMonth}: ${actualUsageSoFar} usage / ${seasonFraction.toFixed(4)} seasonality = ${predicted} predicted`);
  
  return predicted;
};

/**
 * Determines if we're currently in the specified energy year
 * 
 * @param {string} energyYear - The energy year in format 'YYYY-YYYY'
 * @param {Date|null} currentDate - Optional date to use instead of now (for testing)
 * @returns {boolean} Whether we're currently in the specified energy year
 */
export const isCurrentEnergyYear = (energyYear, currentDate = null) => {
  const now = currentDate ? new Date(currentDate) : new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // JS months are 0-indexed
  
  // Calculate the current energy year we're in
  const currentEnergyYearStart = currentMonth >= 7 ? currentYear : currentYear - 1;
  const currentEnergyYear = `${currentEnergyYearStart}-${currentEnergyYearStart + 1}`;
  
  return energyYear === currentEnergyYear;
};

/**
 * Gets the most recent month with data from monthly data object
 * 
 * @param {Object} monthlyData - Object with keys in format 'YYYY-MM' and usage values
 * @returns {string} The latest month in format '01' to '12'
 */
export const getLatestMonthWithData = (monthlyData) => {
  if (!monthlyData || Object.keys(monthlyData).length === 0) {
    return null;
  }
  
  // Get all month keys and sort them chronologically
  const sortedMonthKeys = Object.keys(monthlyData)
    .filter(key => key.match(/^\d{4}-\d{2}$/)) // Ensure keys are in YYYY-MM format
    .map(monthKey => {
      const [year, month] = monthKey.split('-');
      return {
        key: monthKey,
        sortValue: parseInt(year) * 100 + parseInt(month)
      };
    })
    .sort((a, b) => b.sortValue - a.sortValue); // Sort in descending order
  
  if (sortedMonthKeys.length === 0) {
    return null;
  }
  
  // Extract the month part from the latest key
  const latestMonthKey = sortedMonthKeys[0].key;
  return latestMonthKey.split('-')[1];
};